import React from "react";
import { Paper, Group, Text, Stack, Badge, SimpleGrid } from "@mantine/core";
import { JourneyItem } from "./journey.types";
import MouHoursCard from "./MouHoursCard";

const CurrentState = ({
  data,
  batchData,
}: {
  data: JourneyItem;
  batchData?: any;
}) => {
  /* ---------------- Status Color ---------------- */
  // const getStatusColor = (status: string) => {
  //   const colors: Record<string, string> = {
  //     Completed: "bg-green-500",
  //     Hold: "bg-yellow-500",
  //     "On-going": "bg-[#FC7A69]",
  //     "Not Started": "bg-gray-400",
  //     Absconded: "bg-red-500",
  //     Discarded: "bg-[#221E45]",
  //     Leave: "bg-orange-500",
  //     Holiday: "bg-purple-500",
  //   };
  //   return colors[status] || "bg-gray-400";
  // };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Completed: "bg-green-500",
      Hold: "bg-yellow-500",
      "On-going": "bg-[#FC7A69]",
      "Not Started": "bg-gray-400",
      Absconded: "bg-red-500",
      Discarded: "bg-[#221E45]",
      //  REMOVED: Leave: "bg-orange-500", Holiday: "bg-purple-500"
    };
    return colors[status] || "bg-gray-400";
  };
  /* ---------------- Live Attended Hours (LMS → Batch) ---------------- */
  // Attended hours are written to the batch by the LMS webhook, not by Journey.
  // Prefer the live batch value; fall back to the journey snapshot.
  const getLiveHours = (ins: { email: string; instructorClasses?: string }) => {
    const live = batchData?.instructors?.find(
      (b: any) =>
        (b.email || "").toLowerCase() === (ins.email || "").toLowerCase(),
    )?.attendedHours;
    return live || ins.instructorClasses || "0";
  };

  /* ---------------- Attendance Count ---------------- */
  const studentsPresent = data.students.filter(
    (s) => s.attendance === "Present",
  ).length;

  const instructorsPresent = data.instructors.filter(
    (i) => i.attendance === "Present",
  ).length;

  return (
    <Paper
      shadow="md"
      className="p-4 sm:p-6 bg-gradient-to-br from-[#FC7A69]/10 to-[#221E45]/10 border-2 border-[#FC7A69]/30"
      radius="lg"
    >
      {/* HEADER */}
      <Group justify="space-between" mb="md" wrap="wrap" gap="sm">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#FC7A69] animate-pulse" />
          <Text className="text-base sm:text-lg font-bold text-[#221E45]">
            Current State
          </Text>
        </div>

        <Badge
          size="md"
          className={`${getStatusColor(
            data.batchStatus,
          )} text-white text-xs px-3`}
          radius="md"
        >
          {data.batchStatus}
        </Badge>
      </Group>

      {/* STATS */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="sm">
        <Paper
          className="p-3 border border-[#FC7A69]/20 bg-white/50"
          radius="md"
        >
          <Text className="text-xs text-[#221E45]/60 mb-1 font-medium">
            Last Updated
          </Text>
          <Text className="text-sm font-bold text-[#221E45]">
            {new Date(data.journeyDate).toLocaleDateString()}
          </Text>
        </Paper>

        <Paper
          className="p-3 border border-[#FC7A69]/20 bg-white/50"
          radius="md"
        >
          <Text className="text-xs text-[#221E45]/60 mb-1 font-medium">
            Sessions
          </Text>
          <Text className="text-xl font-bold text-[#FC7A69]">
            {data.numSessions}
          </Text>
        </Paper>

        {/* <Paper
          className="p-3 border border-[#FC7A69]/20 bg-white/50"
          radius="md"
        >
          <Text className="text-xs text-[#221E45]/60 mb-1 font-medium">
            Trainer
          </Text>
          <Text className="text-sm font-bold text-[#221E45] truncate">
            {data.trainerName || "N/A"}
          </Text>
          <Text className="text-xs text-[#221E45]/50 truncate">
            {data.trainerEmail || ""}
          </Text>
        </Paper> */}
        <Paper
          className="p-3 border border-[#FC7A69]/20 bg-white/50"
          radius="md"
        >
          <Text className="text-xs text-[#221E45]/60 mb-1 font-medium">
            Trainer
          </Text>
          <Text className="text-sm font-bold text-[#221E45] truncate">
            {data.trainerName || "N/A"}
          </Text>
          <Text className="text-xs text-[#221E45]/50 truncate">
            {data.trainerEmail || ""}
          </Text>
          {/*  NEW: Show active trainer badge */}
          <Badge size="xs" color="blue" variant="light" mt={4}>
            Active Trainer
          </Badge>
        </Paper>

        <Paper
          className="p-3 border border-[#FC7A69]/20 bg-white/50"
          radius="md"
        >
          <Text className="text-xs text-[#221E45]/60 mb-1 font-medium">
            Attendance
          </Text>
          <Stack gap={2}>
            <Text className="text-xs">
              <span className="font-bold text-[#FC7A69]">
                {studentsPresent}
              </span>
              <span className="text-[#221E45]/60">/{data.students.length}</span>
              <span className="ml-1 text-[#221E45]/50">Students</span>
            </Text>

            {/*  UPDATED: Only show active instructor */}
            {data.instructors
              .filter(
                (ins) =>
                  ins.email.toLowerCase() === data.trainerEmail.toLowerCase(),
              )
              .map((ins, idx) => (
                <Text key={idx} className="text-xs">
                  <span className="font-bold text-[#221E45]">{ins.name}</span>
                  <span className="text-[#FC7A69] ml-1">
                    ({getLiveHours(ins)} hrs)
                  </span>
                  <Badge size="xs" color="blue" variant="light" ml={4}>
                    {ins.attendance}
                  </Badge>
                </Text>
              ))}

            {/*  NEW: Show count of previous instructors if any */}
            {data.instructors.length > 1 && (
              <Text size="xs" c="dimmed">
                + {data.instructors.length - 1} previous trainer(s)
              </Text>
            )}
          </Stack>
        </Paper>

        <Paper
          className="p-3 border border-[#FC7A69]/20 bg-white/50"
          radius="md"
        >
          <Text className="text-xs text-[#221E45]/60 mb-1 font-medium">
            Attendance
          </Text>
          <Stack gap={2}>
            <Text className="text-xs">
              <span className="font-bold text-[#FC7A69]">
                {studentsPresent}
              </span>
              <span className="text-[#221E45]/60">/{data.students.length}</span>
              <span className="ml-1 text-[#221E45]/50">Students</span>
            </Text>

            {data.instructors.map((ins, idx) => (
              <Text key={idx} className="text-xs">
                <span className="font-bold text-[#221E45]">{ins.name}</span>
                <span className="text-[#FC7A69] ml-1">
                  ({getLiveHours(ins)} hrs)
                </span>
              </Text>
            ))}
          </Stack>
        </Paper>

        {/* MOU Hours — current trainer from batch data */}
        {batchData && (
          <MouHoursCard batchData={batchData} trainerEmail={data.trainerEmail} />
        )}
      </SimpleGrid>

      {/* CHANGE SUMMARY */}
      {data.changeSummary && (
        <Paper
          className="p-3 mt-4 border-l-4 border-[#FC7A69] bg-white/70"
          radius="md"
        >
          <Text className="text-xs font-semibold text-[#221E45] mb-1">
            Latest Change
          </Text>
          <Text className="text-xs text-[#221E45]/80">
            {data.changeSummary}
          </Text>
        </Paper>
      )}
    </Paper>
  );
};

export default CurrentState;
